import clsx from 'clsx'
import { type ReactElement, useMemo } from 'react'
import { extractIdFromIri } from '~/api/utils'
import { ProfilesList } from '~/components/Global/Elements/ProfilesList/ProfilesList'
import { MessageStatusIcon } from '~/domains/messaging/components/MessageStatusIcon'
import { useResolveStorageUrl } from '~/domains/messaging/hooks/useResolveStorageUrl'
import type { MessageBubbleEvent } from '~/domains/messaging/types'
import {
  EMAIL_STATUS_CONFIG,
  isSmsStatus,
  SMS_STATUS_CONFIG
} from '~/domains/messaging/types'
import { OrderTag } from '~/domains/orders/components/OrderTag/OrderTag'
import { formatFullDateTime, formatTime } from '~/utils/date'
import { sanitizeEmailHtml } from '~/utils/sanitize'
import { EmailAttachments } from './EmailAttachments'
import {
  bubble,
  bubbleAvatarCol,
  bubbleExcerpt,
  bubbleInbound,
  bubbleMeta,
  bubbleMetaInbound,
  bubbleMetaOutbound,
  bubbleOrderInBubble,
  bubbleOutbound,
  bubbleRow,
  bubbleRowContent,
  bubbleRowInbound,
  bubbleRowOutbound,
  bubbleStatus,
  bubbleSubject,
  bubbleTime,
  bubbleWrapper
} from './MessageBubble.css'

const isInbound = (event: MessageBubbleEvent): boolean =>
  event.type === 'sms_received' || event.type === 'email_received'

const CHANNEL_LABEL: Record<MessageBubbleEvent['type'], string> = {
  sms_sent: 'SMS',
  sms_received: 'SMS',
  send_email: 'email',
  email_received: 'email'
}

export const MessageBubble = ({
  event,
  showInlineImages
}: {
  event: MessageBubbleEvent
  showInlineImages: boolean
}): ReactElement => {
  const inbound = isInbound(event)
  const date = event.created
  // Outbound: the user who wrote it. Inbound: the applicant or contact who did; the user on the
  // event is only the mailbox owner or the author of the SMS thread being replied to.
  const userId = inbound
    ? null
    : extractIdFromIri(event.assignedUser ?? event.createdBy)
  const senderIds = (iris?: string[]) =>
    inbound
      ? (iris ?? [])
          .map((iri) => extractIdFromIri(iri))
          .filter((id): id is number => id !== null)
      : []
  const applicantIds = senderIds('applicants' in event ? event.applicants : [])
  const contactIds = senderIds('contacts' in event ? event.contacts : [])

  const statusConfig =
    event.type === 'send_email' || event.type === 'email_received'
      ? event.emailStatus
        ? (EMAIL_STATUS_CONFIG[event.emailStatus] ?? null)
        : null
      : event.type === 'sms_sent' || event.type === 'sms_received'
        ? event.smsStatus && isSmsStatus(event.smsStatus)
          ? SMS_STATUS_CONFIG[event.smsStatus]
          : null
        : null

  const resolveStorageUrl = useResolveStorageUrl()

  const sanitizedExcerpt = useMemo(
    () =>
      (event.type === 'send_email' || event.type === 'email_received') &&
      (event.content ?? event.excerpt)
        ? sanitizeEmailHtml(
            event.content ?? event.excerpt ?? '',
            showInlineImages
              ? new Map(
                  (event.emailInlineImages ?? []).map(({ contentId, url }) => [
                    contentId,
                    resolveStorageUrl(url)
                  ])
                )
              : new Map()
          )
        : null,
    [event, showInlineImages, resolveStorageUrl]
  )

  const channel = CHANNEL_LABEL[event.type]

  return (
    <div
      className={clsx(
        bubbleRow,
        inbound ? bubbleRowInbound : bubbleRowOutbound
      )}
    >
      <div className={bubbleRowContent}>
        <div className={bubbleAvatarCol}>
          {(userId || applicantIds.length > 0 || contactIds.length > 0) && (
            <ProfilesList
              userIds={userId ? [userId] : []}
              applicantIds={applicantIds}
              contactIds={contactIds}
            />
          )}
        </div>
        <div className={bubbleWrapper}>
          <div
            className={clsx(bubble, inbound ? bubbleInbound : bubbleOutbound)}
          >
            {event.order && (
              <div className={bubbleOrderInBubble}>
                <OrderTag order={event.order} />
              </div>
            )}
            {event.type === 'send_email' || event.type === 'email_received' ? (
              <>
                {event.subject && (
                  <div className={bubbleSubject}>{event.subject}</div>
                )}
                {sanitizedExcerpt && (
                  <div
                    className={bubbleExcerpt}
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized by DOMPurify
                    dangerouslySetInnerHTML={{ __html: sanitizedExcerpt }}
                  />
                )}
              </>
            ) : event.type === 'sms_sent' || event.type === 'sms_received' ? (
              <span>{event.smsContent ?? event.notes}</span>
            ) : (
              <span>{event.notes}</span>
            )}
          </div>

          {(event.type === 'send_email' || event.type === 'email_received') &&
            event.emailAttachments &&
            event.emailAttachments.length > 0 && (
              <EmailAttachments attachments={event.emailAttachments} />
            )}

          <div
            className={clsx(
              bubbleMeta,
              inbound ? bubbleMetaInbound : bubbleMetaOutbound
            )}
          >
            {date && (
              <span
                className={bubbleTime}
                title={formatFullDateTime(new Date(date))}
              >
                {formatTime(new Date(date))} via {channel}
              </span>
            )}
            <MessageStatusIcon
              statusConfig={statusConfig}
              className={bubbleStatus}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
